import { Heading, Text, Section, Button, Row, Column } from '@react-email/components';
import EmailLayout from './shared/EmailLayout';
import { ARCHETYPE_COPY, BAND_TONE } from '../schemas/board-ai-literacy.copy';
import { topThreeRecommendations } from '../schemas/board-ai-literacy.recommendations';
import type { ScoredResult, IntakeData } from '../engine/types';
import type { ArchetypeId } from '../engine/archetype';

const BAND_LABEL: Record<string, string> = {
  'at-risk': 'At Risk',
  maturing: 'Maturing',
  mature: 'Mature',
};

const TONE_COLOR: Record<string, string> = {
  danger: '#b42318',
  warning: '#b54708',
  success: '#067647',
};

function Bar({ label, value }: { label: string; value: number }) {
  return (
    <Row>
      <Column style={{ verticalAlign: 'middle', width: 180 }}>
        <Text style={{ fontSize: 14, margin: 0 }}>{label}</Text>
      </Column>
      <Column>
        <div style={{ background: '#eee', height: 12, borderRadius: 6, overflow: 'hidden' }}>
          <div style={{ background: '#1a6e97', height: '100%', width: `${value}%` }} />
        </div>
        <Text style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{value} / 100</Text>
      </Column>
    </Row>
  );
}

interface Props {
  result: ScoredResult;
  intake: IntakeData;
}

export default function BoardAiLiteracyEmail({ result, intake }: Props) {
  const axes = result.score as Record<string, number>;
  const tone = BAND_TONE[result.band] ?? 'warning';
  const arch = result.archetype ? ARCHETYPE_COPY[result.archetype as ArchetypeId] : null;
  const recs = topThreeRecommendations(result.answers);
  const bandLabel = BAND_LABEL[result.band] ?? result.band;

  return (
    <EmailLayout
      preheader={`Your Board AI Literacy result — ${bandLabel}, score ${result.composite}/100`}
    >
      <Heading as="h1" style={{ fontSize: 24 }}>
        Your Board AI Literacy Index result
      </Heading>
      <Text>Hello{intake.name ? ` ${intake.name.split(' ')[0]}` : ''},</Text>

      <Section
        style={{
          borderLeft: `4px solid ${TONE_COLOR[tone]}`,
          padding: '12px 16px',
          background: '#fafafa',
          marginTop: 16,
        }}
      >
        <Text style={{ margin: 0, fontSize: 14, color: '#666' }}>Composite score</Text>
        <Text style={{ margin: 0, fontSize: 36, fontWeight: 700 }}>
          {result.composite ?? 0} / 100
        </Text>
        <Text style={{ margin: 0, fontSize: 14 }}>{bandLabel}</Text>
      </Section>

      <Heading as="h2" style={{ fontSize: 18, marginTop: 24 }}>
        Where you stand on each axis
      </Heading>
      <Bar label="Governance Maturity" value={axes.governance ?? 0} />
      <Bar label="Director Capability" value={axes.capability ?? 0} />

      {arch && (
        <>
          <Heading as="h2" style={{ fontSize: 18, marginTop: 24 }}>
            {arch.title}
          </Heading>
          <Text>{arch.body}</Text>
        </>
      )}

      {recs.length > 0 ? (
        <>
          <Heading as="h2" style={{ fontSize: 18, marginTop: 24 }}>
            Your priorities for the next 90 days
          </Heading>
          {recs.map((r, i) => (
            <Section key={r.questionId} style={{ marginTop: 16 }}>
              <Text style={{ fontWeight: 600 }}>
                {i + 1}. {r.prompt}
              </Text>
              <Text style={{ fontSize: 14, color: '#444' }}>{r.action}</Text>
            </Section>
          ))}
        </>
      ) : (
        <>
          <Heading as="h2" style={{ fontSize: 18, marginTop: 24 }}>
            No urgent gaps to fix
          </Heading>
          <Text style={{ fontSize: 14, color: '#444' }}>
            You answered at the top of every question, so there is no 90-day remediation list. The
            work now is holding the line: keep a quarterly board AI review so you stay ahead as
            ISO/IEC 42001, APRA guidance, and the Voluntary AI Safety Standard evolve.
          </Text>
        </>
      )}

      <Section style={{ marginTop: 32, padding: 16, background: '#f4f8ff', borderRadius: 8 }}>
        <Text style={{ margin: 0, fontWeight: 600 }}>Book a 30-minute Board AI Briefing</Text>
        <Text style={{ margin: '4px 0 12px' }}>
          No prep needed. Bring the report and we will walk through what to fix first.
        </Text>
        <Button
          href={`https://innovatexsolutions.com.au/contact/?source=board-ai-literacy&band=${result.band}${
            result.archetype ? `&archetype=${result.archetype}` : ''
          }`}
          style={{
            background: '#1a6e97',
            color: '#fff',
            padding: '12px 16px',
            borderRadius: 6,
            fontWeight: 600,
          }}
        >
          Book the briefing
        </Button>
      </Section>

      <Text style={{ marginTop: 32 }}>
        Todd Johnson
        <br />
        Principal, InnovateX Solutions
        <br />
        <a href="https://innovatexsolutions.com.au/services/fractional-chief-ai-officer/">
          Fractional CAIO services
        </a>
      </Text>
    </EmailLayout>
  );
}
